/**
 * Web push in the app: registers the service worker (public/sw.js), keeps this device's subscription
 * on the server while someone is signed in, and drives the "Turn on alerts" prompt.
 *
 * What "automatic" can mean depends on the device:
 * - Permission granted: every time the app opens signed in, the subscription is refreshed silently.
 * - Never asked: browsers only show the permission dialog after a tap, so the app offers its own
 *   prompt (PushPrompt) at a good moment and asks on the tap.
 * - iPhone / iPad: push exists only in the app added to the Home Screen (iOS 16.4+), never in a Safari
 *   tab, so a Safari tab offers "Add to Home Screen" first.
 */
import { api } from './api';
import { API_URL } from './config';
import { isStandalone, platform } from './install';
import { navigate } from './router';
import { createStore, useStore } from './store';

export type PushSupport =
  /** push works here */
  | 'supported'
  /** iPhone / iPad in a Safari tab: add to the Home Screen first */
  | 'ios_needs_install'
  /** iOS older than 16.4, or a browser without push */
  | 'unsupported';

export function pushSupport(): PushSupport {
  const hasApi = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (platform() === 'ios' && !isStandalone()) return 'ios_needs_install';
  return hasApi ? 'supported' : 'unsupported';
}

export type Permission = NotificationPermission | 'unsupported';

interface PushState {
  support: PushSupport;
  permission: Permission;
  /** This device has a subscription stored on the server for the signed-in client. */
  subscribed: boolean;
  /** Server has VAPID keys. null = not known yet. */
  serverEnabled: boolean | null;
  /** Something the client just did that makes alerts relevant: show the prompt now. */
  nudge: 'order' | 'price_alert' | 'deposit' | null;
}

const perm = (): Permission => ('Notification' in window ? Notification.permission : 'unsupported');

export const pushStore = createStore<PushState>({ support: pushSupport(), permission: perm(), subscribed: false, serverEnabled: null, nudge: null });

let vapidKey: string | null = null;
let registration: Promise<ServiceWorkerRegistration | null> | null = null;

function b64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Register the service worker once (also used for the app badge and notification taps). */
export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!registration) {
    registration = !('serviceWorker' in navigator)
      ? Promise.resolve(null)
      : navigator.serviceWorker
          .register('/sw.js', { scope: '/' })
          .then(async (reg) => {
            const ready = await navigator.serviceWorker.ready;
            // The worker reports taps to the API; it can't read the page's runtime config itself.
            ready.active?.postMessage({ type: 'config', apiUrl: API_URL || window.location.origin });
            return reg;
          })
          .catch(() => null);
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (e) => {
        if (e.data?.type === 'push-open' && typeof e.data.link === 'string') navigate(e.data.link);
        if (e.data?.type === 'push-resubscribe') void syncPush(true);
      });
    }
  }
  return registration;
}

async function serverConfig(): Promise<boolean> {
  if (pushStore.get().serverEnabled !== null) return !!pushStore.get().serverEnabled;
  try {
    const c = await api.get<{ publicKey: string | null }>('/api/push/key');
    vapidKey = c.publicKey;
    pushStore.set({ serverEnabled: !!c.publicKey });
  } catch {
    return false;
  }
  return !!pushStore.get().serverEnabled;
}

const devicePlatform = () => (platform() === 'other' ? 'desktop' : platform());

async function sendSubscription(sub: PushSubscription) {
  const j = sub.toJSON();
  await api.post('/api/push/subscriptions', {
    endpoint: j.endpoint,
    keys: { p256dh: j.keys?.p256dh, auth: j.keys?.auth },
    platform: devicePlatform(),
    standalone: isStandalone(),
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
}

/**
 * Signed in and permission already granted: make sure this device's subscription is on the server
 * (no UI). `force` subscribes again even when the browser already has a subscription.
 */
export async function syncPush(force = false): Promise<void> {
  pushStore.set({ support: pushSupport(), permission: perm() });
  if (pushSupport() !== 'supported' || perm() !== 'granted' || optedOut()) return;
  if (!(await serverConfig()) || !vapidKey) return;
  const reg = await registerServiceWorker();
  if (!reg) return;
  try {
    let sub = await reg.pushManager.getSubscription();
    if (sub && force) {
      await sub.unsubscribe().catch(() => {});
      sub = null;
    }
    sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(vapidKey) });
    await sendSubscription(sub);
    pushStore.set({ subscribed: true });
  } catch {
    pushStore.set({ subscribed: false });
  }
}

/** Call from a tap: shows the browser's permission dialog, then subscribes. */
export async function enablePush(): Promise<Permission> {
  if (pushSupport() !== 'supported') return 'unsupported';
  // iOS needs the request inside the tap handler, before any other await.
  const result = await Notification.requestPermission();
  pushStore.set({ permission: result, nudge: null });
  if (result === 'granted') {
    setOptOut(false);
    await syncPush();
  }
  return result;
}

/** Turn push off on this device (kept off until the client turns it on again here). */
export async function disablePush(): Promise<void> {
  const reg = await registerServiceWorker();
  const sub = await reg?.pushManager.getSubscription().catch(() => null);
  if (sub) {
    await api.del(`/api/push/subscriptions?endpoint=${encodeURIComponent(sub.endpoint)}`).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  }
  setOptOut(true);
  pushStore.set({ subscribed: false });
}

/** Before signing out: this device stops getting the client's alerts. */
export async function detachPush(): Promise<void> {
  try {
    const reg = await (registration ?? Promise.resolve(null));
    const sub = await reg?.pushManager.getSubscription();
    if (sub) await api.del(`/api/push/subscriptions?endpoint=${encodeURIComponent(sub.endpoint)}`);
  } catch {
    /* signing out anyway */
  }
  pushStore.set({ subscribed: false });
}

// ---- prompt bookkeeping (this browser only) ----

const DISMISS_KEY = 'pushPromptDismissedAt';
const OPT_OUT_KEY = 'pushOptOut';
/** "Not now" hides the prompt for a week. */
const SNOOZE_MS = 7 * 86_400_000;

function readNum(key: string): number {
  try {
    return Number(localStorage.getItem(key) ?? 0) || 0;
  } catch {
    return 0;
  }
}
export function snoozePrompt() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* storage off: it shows again next visit */
  }
  pushStore.set({ nudge: null });
}
export const promptSnoozed = () => Date.now() - readNum(DISMISS_KEY) < SNOOZE_MS;
export function setOptOut(on: boolean) {
  try {
    if (on) localStorage.setItem(OPT_OUT_KEY, '1');
    else localStorage.removeItem(OPT_OUT_KEY);
  } catch {
    /* ignore */
  }
}
export const optedOut = () => readNum(OPT_OUT_KEY) === 1;

/** Something happened that alerts would help with: offer them (unless snoozed or decided). */
export function nudgePush(reason: NonNullable<PushState['nudge']>) {
  pushStore.set({ nudge: reason });
}

export const usePushState = () => useStore(pushStore, (s) => s);
